import { isUndefined } from 'lodash';
import { jStat } from 'jstat';
import Button from 'react-bootstrap/Button';
import { CircularProgress, Button as MuiButton } from '@mui/material';
import Spinner from 'react-bootstrap/Spinner';
import firebaseApp from './firebase';
import { RULES, UPDATE_TYPE, ROLES, archiveRegex } from './Constant';
import moment from 'moment-timezone';
import { filterEverFlagsByConfig } from './everFlagsFilter';

export const randomId = () => {
  return Math.random().toString(36).substring(2, 15);
};

// Create an Error with custom message and code
export function CustomError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

export async function apiRequest(path, method = 'GET', data, auth = firebaseApp.auth()) {
  const accessToken = auth.currentUser ? await auth.currentUser.getIdToken() : undefined;

  return fetch(`/api/${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`
    },
    body: data ? JSON.stringify(data) : undefined
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 'error') {
        throw new CustomError(response.code, response.message);
      } else {
        return response.data;
      }
    });
}

export const textSort = (a, b) => {
  if (a.label < b.label) {
    return -1;
  }
  if (a.label > b.label) {
    return 1;
  }
  return 0;
};

export const randomHexColorCode = () => {
  const n = (Math.random() * 0xfffff * 1000000).toString(16);
  return `#${n.slice(0, 6)}`;
};

export const randomDarkColor = () => {
  return 'hsla(' + Math.floor(Math.random() * 360) + ', 100%, 25%, 1)';
};

export const randomLightColor = (number) => {
  const hue = number * 137.508;
  return `hsl(${hue},50%,50%)`;
};

export const arrayStandardDeviation = (arrIn) => {
  let arr = arrIn;
  // Creating the mean with Array.reduce
  const mean =
    arr.reduce((acc, curr) => {
      return acc + curr;
    }, 0) / arr.length;

  // Assigning (value - mean) ^ 2 to every array item
  arr = arr.map((k) => {
    return (k - mean) ** 2;
  });

  // Calculating the sum of updated array
  const sum = arr.reduce((acc, curr) => acc + curr, 0);

  // Returning the Standard deviation
  return Math.sqrt(sum / arr.length);
};

// Sentinel strings used in upstream calculations / spreadsheet exports to mean
// "no numeric value here". They must be stripped before passing data to Recharts
// — see src/pages/chart.js for the consumer.
export const NUMERIC_PLACEHOLDER_VALUES = new Set([
  '#N/A',
  '#DIV/0!',
  '#VALUE!',
  '#REF!',
  '#NAME?',
  '#NULL!',
  '#NUM!',
  'N/A',
  '---'
]);

export const isNumericPlaceholder = (v) => {
  if (typeof v !== 'string') return false;
  return NUMERIC_PLACEHOLDER_VALUES.has(v.trim());
};

// A `result` the lab typed that reads like a botched number: it contains a digit
// and is not blank, but is not a valid number (`Number(...)` is NaN) and is not a
// deliberate comparison qualifier ("<0.5", ">100"). These are almost always
// data-entry typos ("3.24v", "10..5", "16.3.4", ",0206") that the calc silently
// drops and the sig-digit formatter used to mask into a clean number. Pure-text
// annotations ("N/A", "inst down", "#REF!") and blanks contain no digit, so they
// are intentional and left alone; the chart highlights/flags only what this
// returns true for, so the lab can find and correct the real mistakes.
export const isMalformedNumericResult = (value) => {
  if (typeof value !== 'string') return false;
  const s = value.trim();
  if (s === '') return false;
  if (/\s/.test(s)) return false; // internal whitespace => a phrase ("5 ppm", "inst 2 down"), not a typo'd number
  if (!/[0-9]/.test(s)) return false; // pure-text annotation, not a typo'd number
  if (/^[<>≤≥~]/.test(s)) return false; // deliberate detection-limit qualifier
  return Number.isNaN(Number(s)); // a bare digit-bearing token that isn't a valid number
};

// Filter a chart-lines array by the saved Control Strategy. Lowercases the
// strategy first so legacy capitalizations ("Control Limits" with a capital L
// have been seen in the wild on shared Firestore configs) still match the
// canonical lowercase forms below. An unknown / missing strategy falls back
// to the EWMA filter — the system default in
// getDefaultControlStrategyConfiguration (src/util/db.js) — instead of
// returning [], which would render no Lines in the I-chart.
export const filterChartLinesByStrategy = (strategy, chartLines) => {
  const normalized = typeof strategy === 'string' ? strategy.toLowerCase() : '';
  switch (normalized) {
    case 'ewma':
      return chartLines.filter((line) => line.ewma);
    case 'run rules':
      return chartLines.filter((line) => line.runRules);
    case 'control limits':
      return chartLines.filter((line) => line.controlLimits);
    case 'tolerance limits':
      return chartLines.filter((line) => line.toleranceLimits);
    default:
      if (strategy != null && strategy !== '') {
        // Surface unrecognized values so misconfigured Firestore docs are
        // visible in the browser console / error monitoring.
        // eslint-disable-next-line no-console
        console.warn(
          `[chart] Unknown controlStrategy ${JSON.stringify(strategy)} — falling back to EWMA`
        );
      }
      return chartLines.filter((line) => line.ewma);
  }
};

// Single source of truth for whether a data point belongs in an SQC chart series.
// A point is excluded ONLY when it is omitted from SQC (`omitFromSqc === 'Y'`).
//
// Silenced points (`silence === 'Y'`) are NOT excluded: validated against the
// deprecated SQC Google Sheet, the omissions-removed dataset that feeds every
// calculation is gated only on Omit SQC (Data!BA `=IF(I11="N",A11,"")`), never on
// Silence. Silence only suppresses the point's flags/notifications (Flags!F gate),
// so a silenced point must still plot — as a green (non-flagged) point via
// `createCustomizedDot`, which renders silenced points green regardless of any
// stored flag columns. `omitFromSqc` is constrained to 'Y'/'N'; the
// case-insensitive string compare is defensive.
//
// The other places that consume silenced points already suppress their flags
// per-row, so they don't render red now that silenced points flow back into the
// series: the data grid excludes silence from its `row-ooc` red class and hides
// its flag text for silenced rows (chart.js addRowClass / flag renderCell), the
// ever-flags path is short-circuited to green in `createCustomizedDot` before its
// ever-flag branches, and the instrument-comparison overlay colours dots by
// instrument (not by flags), so it never renders a silenced point red.
// Re-exported from the dependency-free ./sqcInclusion module so the pure
// instrumentStats module can share the exact same rule without importing util.js's
// heavier React/MUI/Firebase dependency tree.
export { isIncludedInSqcChartSeries } from './sqcInclusion';
export { filterEverFlagsByConfig } from './everFlagsFilter';

export const arrayAverage = (arrIn) => {
  const arr = arrIn
    .filter((a) => !Number.isNaN(Number(a)) && a != null && !isNumericPlaceholder(a))
    .map(Number);

  if (arr.length === 0) return 0;

  return arr.reduce((a, b) => a + b, 0) / arr.length;
};

export const calculateHistogram = (chartData, dataMin, dataMax) => {
  // populate bin, count, normalPDF
  const n = chartData.length;
  if (n < 10) {
    // no histogram
    return null;
  }

  const histogramData = [];

  // number of bins
  const k = 1 + 3.322 * Math.log10(n);
  const interval = (dataMax - dataMin) / k;
  const dataValues = chartData.map((cd) => Number(cd.resultOmits));
  const dataAvg = arrayAverage(dataValues);
  const dataStd = arrayStandardDeviation(dataValues);

  for (let i = 1; i <= k; i += 1) {
    const binStart = dataMin + (i - 1) * interval;
    const binEnd = dataMin + i * interval;
    const binItems = chartData
      .filter((cd) => cd.resultOmits >= binStart && cd.resultOmits <= binEnd)
      .map((cd) => cd.resultOmits);

    const binZ = (binEnd - dataAvg) / dataStd;
    const binP = jStat.normal.pdf(binZ, 0, 1);
    histogramData.push({
      bin: i,
      count: binItems.length,
      normalPDF: Math.round(binP * n * 1000) / 1000
    });
  }

  return histogramData;
}; // calculateHistogram

export function linearRegression(y, x) {
  const lr = {};
  const n = y.length;
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  let sumYY = 0;

  for (let i = 0; i < y.length; i += 1) {
    sumX += x[i];
    sumY += y[i];
    sumXY += x[i] * y[i];
    sumXX += x[i] * x[i];
    sumYY += y[i] * y[i];
  }

  lr.slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  lr.intercept = (sumY - lr.slope * sumX) / n;
  // eslint-disable-next-line no-restricted-properties
  lr.r2 = Math.pow(
    (n * sumXY - sumX * sumY) / Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY)),
    2
  );

  return lr;
}

export const checkReadOnlyTable = (table) => {
  if (table) {
    if (!!table[RULES.CREATE]) {
      return false;
    }
    if (!!table[RULES.DELETE]) {
      return false;
    }
    if (table[RULES.UPDATE] && table[RULES.UPDATE].type !== UPDATE_TYPE.NONE) {
      return false;
    }
  }
  return true;
};

export const checkReadOnly = (permissionData, table) => {
  if (permissionData && permissionData[table]) {
    return checkReadOnlyTable(permissionData[table]);
  }
  return true;
};

export const checkDelete = (permission, table) => {
  if (permission) {
    const isAdmin = permission && permission.permissionType === ROLES.ADMIN;
    if (isAdmin) {
      return isAdmin;
    }
    if (permission[table]) {
      if (!!permission[table][RULES.DELETE]) {
        return true;
      }
    }
  }
  return false;
};

export const checkCreate = (permission, table) => {
  if (permission) {
    const isAdmin = permission && permission.permissionType === ROLES.ADMIN;
    if (isAdmin) {
      return isAdmin;
    }
    if (permission[table]) {
      if (!!permission[table][RULES.CREATE]) {
        return true;
      }
    }
  }
  return false;
};

export const checkUpdate = (permission, table) => {
  if (permission) {
    const isAdmin = permission && permission.permissionType === ROLES.ADMIN;
    if (isAdmin) {
      return isAdmin;
    }
    if (permission[table]) {
      if (
        permission[table][RULES.UPDATE] &&
        permission[table][RULES.UPDATE].type !== UPDATE_TYPE.NONE
      ) {
        return true;
      }
    }
  }
  return false;
};

export const checkHasPermission = (permission, type) => {
  // No permission object → no access at all
  if (!permission) return false;

  if (type === RULES.VIEW) {
    return permission?.[RULES.VIEW] !== false;
  }

  if (type === RULES.SAVE) {
    return !checkReadOnlyTable(permission);
  }
  if (type === RULES.CREATE) {
    return permission[RULES.CREATE] === true;
  }
  if (type === RULES.DELETE) {
    return permission[RULES.DELETE] === true;
  }
  if (type === RULES.UPDATE) {
    return permission[RULES.UPDATE] && permission[RULES.UPDATE].type !== UPDATE_TYPE.NONE;
  }
  return false;
};

export const isAdmin = (permission) => {
  if (permission) {
    const isAdmin = permission && permission.permissionType === ROLES.ADMIN;
    return isAdmin;
  }
  return false;
};

export const isContributor = (permission) => {
  if (permission) {
    const isContributor = permission && permission.permissionType === ROLES.CONTRIBUTION;
    return isContributor;
  }
  return false;
};

export const renderButtonWithPermissions = (
  buttonText,
  currentFunction,
  table,
  rule,
  permission,
  hasLoading = false,
  isLoading = false
) => {
  const isAdmin = permission && permission.permissionType === ROLES.ADMIN;
  if (isAdmin) {
    return (
      <Button disabled={hasLoading && isLoading} onClick={currentFunction}>
        {hasLoading && isLoading ? (
          <Spinner
            as="span"
            animation="border"
            size="sm"
            role="status"
            aria-hidden="true"
          ></Spinner>
        ) : (
          <span>{buttonText}</span>
        )}
      </Button>
    );
  }
  if (permission && permission[table]) {
    const hasPerMission = checkHasPermission(permission[table], rule);
    if (hasPerMission) {
      return (
        <Button onClick={currentFunction} disabled={hasLoading && isLoading}>
          {hasLoading && isLoading ? (
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
            ></Spinner>
          ) : (
            <span>{buttonText}</span>
          )}
        </Button>
      );
    }
  }
  return <></>;
};

export const renderLoadingButton = ({
  buttonText,
  hasLoading = false,
  isLoading = false,
  ...otherProps
}) => {
  return (
    <Button disabled={hasLoading && isLoading} {...otherProps}>
      {hasLoading && isLoading ? (
        <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true"></Spinner>
      ) : (
        <span>{buttonText}</span>
      )}
    </Button>
  );
};

export const renderMUIButtonWithPermissions = (
  buttonText,
  currentFunction,
  table,
  rule,
  permission,
  isLoading = false
) => {
  const isAdmin = permission && permission.permissionType === ROLES.ADMIN;
  if (isAdmin) {
    return (
      <MuiButton variant="contained" disabled={isLoading} onClick={currentFunction}>
        {isLoading ? <CircularProgress size="1.5rem" /> : <span>{buttonText}</span>}
      </MuiButton>
    );
  }
  if (permission && permission[table]) {
    const hasPerMission = checkHasPermission(permission[table], rule);
    if (hasPerMission) {
      return (
        <MuiButton variant="contained" disabled={isLoading} onClick={currentFunction}>
          {isLoading ? <CircularProgress size="1.5rem" /> : <span>{buttonText}</span>}
        </MuiButton>
      );
    }
  }
  return <></>;
};

export const renderMUILoadingButton = ({
  buttonText,
  isLoading = false,
  disabled,
  ...otherProps
}) => {
  return (
    <MuiButton disabled={isLoading || disabled} {...otherProps}>
      {isLoading ? <CircularProgress size="1.5rem" /> : <span>{buttonText}</span>}
    </MuiButton>
  );
};

export const renderAuthButtonWithPermission = (
  handleResults,
  auth,
  currentPersonnel,
  permission,
  table,
  rule,
  loading,
  title
) => {
  if (
    (permission && permission.permissionType === ROLES.ADMIN) ||
    (permission && permission[table] && checkHasPermission(permission[table], rule))
  ) {
    return (
      <div>
        {' '}
        {auth.renderAddToFileWithPermissions(handleResults, auth, currentPersonnel, loading, title)}
      </div>
    );
  }
};

export const uniqueArrayByKey = (arr, key) => {
  const uniqueMap = new Map();
  arr.forEach((obj) => {
    if (obj != null) {
      const keyValue = obj[key];
      if (!uniqueMap.has(keyValue)) {
        uniqueMap.set(keyValue, obj);
      }
    }
  });
  return Array.from(uniqueMap.values());
};

export const setDefaultControlStrategies = (key, data) => {
  switch (key) {
    case 'EWMA':
      data.controlLimitViolation = 'Out of Control';
      data.warningLimitViolation = 'Off';
      data.twoOfThree = 'Off';
      data.fiveConsecutive = 'Off';
      data.nineConsecutive = 'Out of Control';
      data.sevenConsecutive = 'Off';
      data.ewmaTrend = 'Out of Control';
      data.controlLimitExceedance = 'Off';
      data.toleranceLimitExceedance = 'Off';
      data.singlePointViolation = 'Warning';
      data.fivePointViolation = 'Out of Control';
      data.andersonDarling = 'Off';
      data.tpiOnePointTwo = 'Off';
      break;
    case 'Run rules':
      data.controlLimitViolation = 'Out of Control';
      data.warningLimitViolation = 'Off';
      data.twoOfThree = 'Out of Control';
      data.fiveConsecutive = 'Out of Control';
      data.nineConsecutive = 'Out of Control';
      data.sevenConsecutive = 'Out of Control';
      data.ewmaTrend = 'Off';
      data.controlLimitExceedance = 'Off';
      data.toleranceLimitExceedance = 'Off';
      data.singlePointViolation = 'Warning';
      data.fivePointViolation = 'Out of Control';
      data.andersonDarling = 'Off';
      data.tpiOnePointTwo = 'Off';
      break;
    case 'Control limits':
      data.controlLimitViolation = 'Off';
      data.warningLimitViolation = 'Off';
      data.twoOfThree = 'Off';
      data.fiveConsecutive = 'Off';
      data.nineConsecutive = 'Off';
      data.sevenConsecutive = 'Off';
      data.ewmaTrend = 'Off';
      data.controlLimitExceedance = 'Out of Control';
      data.toleranceLimitExceedance = 'Off';
      data.singlePointViolation = 'Off';
      data.fivePointViolation = 'Off';
      data.andersonDarling = 'Off';
      data.tpiOnePointTwo = 'Off';
      break;
    default:
      data.controlLimitViolation = 'Off';
      data.warningLimitViolation = 'Off';
      data.twoOfThree = 'Off';
      data.fiveConsecutive = 'Off';
      data.nineConsecutive = 'Off';
      data.sevenConsecutive = 'Off';
      data.ewmaTrend = 'Off';
      data.controlLimitExceedance = 'Off';
      data.toleranceLimitExceedance = 'Out of Control';
      data.singlePointViolation = 'Off';
      data.fivePointViolation = 'Off';
      data.andersonDarling = 'Off';
      data.tpiOnePointTwo = 'Off';
      break;
  }
};

export const generateControlStrategyData = (
  updatedCurrentControlStrategyConfigurations,
  variation = ''
) => {
  return [
    {
      id: randomId(),
      variable: 'Control limit violation (3 sigma)',
      value: updatedCurrentControlStrategyConfigurations.controlLimitViolation
    },
    {
      id: randomId(),
      variable: 'Warning limit violation (2 sigma)',
      value: updatedCurrentControlStrategyConfigurations.warningLimitViolation
    },
    {
      id: randomId(),
      variable: '2 of 3 points > 2 sigma',
      value: updatedCurrentControlStrategyConfigurations.twoOfThree
    },
    {
      id: randomId(),
      variable: '5 consecutive points > 1 sigma',
      value: updatedCurrentControlStrategyConfigurations.fiveConsecutive
    },
    {
      id: randomId(),
      variable: '9 consecutive points above/below centerline',
      value: updatedCurrentControlStrategyConfigurations.nineConsecutive
    },
    {
      id: randomId(),
      variable: '7 consecutive points increasing / decreasing',
      value: updatedCurrentControlStrategyConfigurations.sevenConsecutive
    },
    {
      id: randomId(),
      variable: 'EWMA trend',
      value: updatedCurrentControlStrategyConfigurations.ewmaTrend
    },
    {
      id: randomId(),
      variable:
        variation === 'spc'
          ? 'User defined specification limit exceedance'
          : 'User defined control limit exceedance',
      value: updatedCurrentControlStrategyConfigurations.controlLimitExceedance
    },
    {
      id: randomId(),
      variable: 'Tolerance limit exceedance',
      value: updatedCurrentControlStrategyConfigurations.toleranceLimitExceedance
    },
    {
      id: randomId(),
      variable: 'Moving range limit violation (single point)',
      value: updatedCurrentControlStrategyConfigurations.singlePointViolation
    },
    {
      id: randomId(),
      variable: 'Moving range limit violation (5 of 20 points)',
      value: updatedCurrentControlStrategyConfigurations.fivePointViolation
    },
    {
      id: randomId(),
      variable: 'Anderson Darling exceeds exceeds 1.13',
      value: updatedCurrentControlStrategyConfigurations.andersonDarling
    },
    {
      id: randomId(),
      variable: 'TPI less than D6792 Limits',
      value: updatedCurrentControlStrategyConfigurations.tpiOnePointTwo
    }
  ];
};

// True when a summary carries an ACTIVE flag of the given kind in the current
// window. `warnings_or`/`ooc_or` are the server's unique-flag lists collected from
// NON-silenced points across the charted data (silence is gated server-side), so
// they already mean "active flag in the window". They are usually arrays but can be
// strings; an empty array is truthy in JS, so a plain `if (summary.warnings_or)`
// wrongly counts in-control charts — always test length. kind: 'ooc' | 'warning'.
export const summaryHasFlag = (summary, kind) => {
  const value = summary?.[kind === 'ooc' ? 'ooc_or' : 'warnings_or'];
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value && String(value).length > 0);
};

// Build the dashboard OOC/Warning KPI rows + per-group counts from CURRENT flags
// (summaryHasFlag), grouped by instrument (non-US) or method (US). Replaces the
// earlier everFlags-of-silenced-points logic so a chart whose flagged points are
// silenced/omitted — i.e. currently in control — no longer shows as OOC/Warning
// (Arteco/Thijs). kind: 'ooc' | 'warning'.
export const buildFlagKpi = (summaries, selectedLocation, isNonUs, kind) => {
  const scoped = selectedLocation
    ? (summaries || []).filter((s) => s.location === selectedLocation)
    : summaries || [];
  const rows = scoped.filter((s) => summaryHasFlag(s, kind));
  const counts = new Map();
  rows.forEach((s) => {
    const key = isNonUs ? s.instrument : s.method;
    if (!key || !s.parameter || !s.location) return;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return { rows, counts };
};

// Which `summary` docs the SQC dashboard (Details tab + KPI Control Status/OOC/
// Warning/TPI tables) should display. Dated archive snapshots (id ending in a
// `_YYYY` / `_YYYY-MM-DD` assessment date, per archiveRegex) are excluded; current
// summaries — including combined-Z-score datasets — are kept. Combined-Z rows were
// previously stripped by a `!id.includes('Combined Z-score')` filter (Arteco/Thijs).
export const includeSummaryInDashboard = (id) => !archiveRegex.test(String(id ?? ''));

export const createCustomizedDot = (
  cx,
  cy,
  payload,
  setupDataChecked,
  isWarning,
  isOOC,
  warningFlagsChecked,
  outOfControlChecked,
  everFlagsChecked,
  isEverFlagsWarning,
  isEverFlagsOOC
) => {
  if (!cx || !cy) {
    return null;
  }
  const dotToReturn = [];
  // Silenced points stay in the chart series and in all calculations, but their
  // live flags/notifications are suppressed, so they normally render green even if
  // stale flag columns are stored on the row.
  //
  // EXCEPTION (Arteco/Thijs): the EverFlags overlay is a historical "ever had a
  // flag" view. When it is on (everFlagsChecked), everFlags OOC/warning colouring
  // must win — including on silenced points — so the everFlags branches are
  // evaluated BEFORE the silence short-circuit below. OOC takes precedence over
  // warning. When EverFlags is off, silence -> green still holds.
  const isSilenced = String(payload?.silence).toUpperCase() === 'Y';
  if (Number(payload.setupData) === 1 && setupDataChecked) {
    dotToReturn.push(renderDot(cx - 3, cy - 3, '#B0E0E6'));
  } else if (everFlagsChecked && isEverFlagsOOC) {
    dotToReturn.push(renderDot(cx - 3, cy - 3, 'red'));
  } else if (everFlagsChecked && isEverFlagsWarning) {
    dotToReturn.push(renderDot(cx - 3, cy - 3, 'yellow'));
  } else if (isSilenced) {
    dotToReturn.push(renderDot(cx - 3, cy - 3, 'green'));
  } else if (isWarning && !isOOC && warningFlagsChecked) {
    dotToReturn.push(renderDot(cx - 3, cy - 3, 'yellow'));
  } else if (!isWarning && isOOC && outOfControlChecked) {
    dotToReturn.push(renderDot(cx - 3, cy - 3, 'red'));
  } else if (isWarning && isOOC && warningFlagsChecked && outOfControlChecked) {
    dotToReturn.push(renderDot(cx - 3, cy - 3, 'red'));
  } else if (isWarning && isOOC && !warningFlagsChecked && outOfControlChecked) {
    dotToReturn.push(renderDot(cx - 3, cy - 3, 'red'));
  } else if (isWarning && isOOC && warningFlagsChecked && !outOfControlChecked) {
    dotToReturn.push(renderDot(cx - 3, cy - 3, 'yellow'));
  } else {
    dotToReturn.push(renderDot(cx - 3, cy - 3, 'green'));
  }
  return dotToReturn;
};

const renderDot = (cx, cy, fill) => {
  return (
    <svg x={cx} y={cy} width={15} height={15} fill={fill} viewBox="0 0 1024 1024" key={randomId()}>
      <path d="M512 256C512 397.4 397.4 512 256 512C114.6 512 0 397.4 0 256C0 114.6 114.6 0 256 0C397.4 0 512 114.6 512 256z" />
    </svg>
  );
};

export const getFacilityLabelByValue = (locations, locationCode) => {
  if (locations && locations.size > 0) {
    const locationData = locations.get(locationCode);
    if (locationData) {
      return locationData.locationName;
    }
  }
  return locationCode;
};

export const dateTimeComparator = (v1, v2) => {
  const d1 = new Date(v1).getTime();
  const d2 = new Date(v2).getTime();
  return d1 - d2;
};

export const firebaseClean = (name) => {
  return String(name).replace(/[.#$/[\]]/g, '');
};

export function alphaNumericSorter(a, b) {
  return String(a.name).localeCompare(String(b.name), undefined, { numeric: true });
}

export function alphaNumericSorterArrayString(a, b) {
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

export function formatCurrency(number) {
  if (!number) {
    return '';
  }
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',

    // These options are needed to round to whole numbers if that's what you want.
    minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 0 // (causes 2500.99 to be printed as $2,501)
  });
  return formatter.format(number);
}

export const dateFormat = (date) => (date.includes('/') ? 'M/D/YYYY' : 'YYYY-MM-DD');

export const setCurrentTime = (dateOnly) =>
  dateOnly
    .set('hours', moment().get('hours'))
    .set('minutes', moment().get('minutes'))
    .set('seconds', moment().get('seconds'));

export const getSigDigitsUtil = (stringValue, chartSigDigits) => {
  const valueToReturn = parseFloat(stringValue);
  if (Number.isNaN(valueToReturn)) {
    return '';
  }
  // Handle edge case of 0 significant digits
  if (chartSigDigits <= 0) {
    if (valueToReturn < 1) {
      return '0';
    }
    return valueToReturn.toPrecision(1);
  }
  if (valueToReturn < 1) {
    return valueToReturn.toFixed(chartSigDigits - 1);
  }
  return valueToReturn.toPrecision(chartSigDigits);
};

// Format the numeric result/limit fields of a chart data row to the key's Chart
// Axis Significant Digits, matching how `result` is already shown. Average
// (`mean`), LCL (`sd3n`) and UCL (`sd3p`) otherwise render at raw float precision
// (13+ decimals). Returns a shallow copy — non-numeric / blank / missing fields
// are left untouched (e.g. "#N/A" moving-range limits on the first observation).
export const SIG_DIGIT_ROW_FIELDS = ['result', 'resultOmits', 'mean', 'sd3n', 'sd3p'];

export const formatRowSigDigits = (row, chartSigDigits, fields = SIG_DIGIT_ROW_FIELDS) => {
  if (!row || typeof row !== 'object') return row;
  const formatted = { ...row };
  fields.forEach((field) => {
    const value = formatted[field];
    // Only reformat genuine numbers. Using Number() (not parseFloat) so a typo'd
    // result like "3.24v" — parseFloat would silently clip to 3.24 and hide the
    // mistake — is left verbatim for the lab to see and correct. Blanks,
    // whitespace-only and placeholders ("#N/A") are likewise untouched (trim
    // first so a " " cell isn't coerced to 0). A genuine 0 IS formatted ("0.00"),
    // matching how every other numeric value renders to sig digits.
    const token = typeof value === 'string' ? value.trim() : value;
    if (token !== '' && token != null && !Number.isNaN(Number(token))) {
      formatted[field] = getSigDigitsUtil(value, chartSigDigits);
    }
  });
  return formatted;
};

export const getTimeZone = (featureFlags) => {
  // timezone selection; could be moved out somewhere else if used more widely
  let tz = 'UTC';
  for (const featureFlag of featureFlags) {
    if (featureFlag.startsWith('tz=')) {
      tz = featureFlag.substring(3);
    }
  }
  return tz;
  // end timezone selection
};

// Date/time text for a chart hover popup. Mirrors the results table: the row's
// `dateTime` timestamp rendered in the site's timezone with the table's format.
// Rows without a timestamp fall back to their date-only `date`; returns '' if neither.
export const CHART_POPUP_DATE_TIME_FORMAT = 'MM/DD/YYYY HH:mm';

export const formatChartPointDateTime = (
  row,
  tz = 'UTC',
  dateTimeFormat = CHART_POPUP_DATE_TIME_FORMAT
) => {
  if (!row) return '';
  if (row.dateTime) {
    const parsed = moment(row.dateTime);
    if (parsed.isValid()) {
      return parsed.tz(tz).format(dateTimeFormat);
    }
  }
  return row.date ? String(row.date) : '';
};

// Popup header for the standard Recharts tooltip: the existing header text, then the
// point's date/time on its own line (same layout as the custom EverFlags popup).
// Returns the text untouched when there is no date/time to show.
export const withPopupDateTime = (text, pointDateTime) =>
  pointDateTime ? (
    <>
      {text}
      <br />
      {pointDateTime}
    </>
  ) : (
    text
  );

export const sanitizePropertyData = (property) => {
  if (isUndefined(property)) {
    return '';
  }
  return property;
};

export const returnLocationsAssigned = (locations = [], setupGroupsIn = [], auth = []) => {
  const setupGroupsAssigned = [];
  const locationsAssigned = [];
  const locationsToReturn = [];
  setupGroupsIn.forEach((setupGroup) => {
    const matchingUser = setupGroup.personnel.find(
      (person) => person.email?.toLowerCase() === auth.user.email?.toLowerCase()
    );
    if (matchingUser) {
      setupGroupsAssigned.push(setupGroup);
    }
  });
  setupGroupsAssigned.forEach((assignedSetupGroup) => {
    assignedSetupGroup.locations.forEach((location) => {
      locationsAssigned.push(location.value);
    });
  });
  locations.forEach((location) => {
    const foundLocation = locationsAssigned.find(
      (assignedLocation) => assignedLocation === location.name
    );
    if (foundLocation) {
      locationsToReturn.push(location);
    }
  });
  return locationsToReturn;
};

export const handleDateTimeColumns = (columns, tz, dateTimeFormat) => {
  return columns.map((item) => {
    if (item.field === 'dateTime' && tz && dateTimeFormat) {
      return {
        ...item,
        renderCell: (params) => moment(params.value).tz(tz).format(dateTimeFormat)
      };
    }
    return item;
  });
};

export const showForAdminView = (permissionData, screen) => {
  if (!permissionData) return false;
  if (isAdmin(permissionData)) return true;
  return permissionData[screen]?.view || false;
};

export const artecoCustomParameterSort = (parameters) => {
  const customArtecoSort = (s) => {
    if (s === 'Reserve Alkalinity - Start pH') return 0;
    else if (s === 'Reserve Alkalinity - pH 7_0') return 1;
    else if (s === 'Reserve Alkalinity - pH 5_5') return 2;
    else if (s === 'Reserve Alkalinity - Inflection Point') return 3;
    else return 4;
  };

  return parameters.slice().sort((a, b) => customArtecoSort(a) - customArtecoSort(b));
};

// Flatten a chart row's `everFlags` into a de-duplicated list of flag strings.
// Handles both shapes: an array of strings, or an array of { version, flags }
// objects (flags collected from ALL versions so none are missed, fixes #51).
// When the key's live Control Strategy config is passed, the stored flags are
// re-evaluated against it (filterEverFlagsByConfig): codes whose rule is now Off
// are dropped and severity follows the live rule, so flags frozen on locked rows
// from a stale config snapshot don't show.
export const getEverFlagsString = (data, liveConfig) => {
  const everFlags = data?.everFlags;

  if (liveConfig) {
    return filterEverFlagsByConfig(everFlags, liveConfig);
  }

  if (!Array.isArray(everFlags) || everFlags.length === 0) {
    return [];
  }

  let flags = [];

  // Case 1: everFlags is array of strings
  if (typeof everFlags[0] === 'string') {
    flags = everFlags;
  }
  // Case 2: everFlags is array of objects with version + flags
  else {
    flags = everFlags
      .filter((item) => item && typeof item.flags === 'string')
      .map((item) => item.flags);
  }

  // Split by `;`, trim, remove empty & duplicates
  return Array.from(
    new Set(
      flags
        .join(';')
        .split(';')
        .map((f) => f.trim())
        .filter(Boolean)
    )
  );
};

// Classify a row's everFlags by the prefix convention used in getEverFlagsString:
// entries beginning "OOC:" are out-of-control; "Warnings:"/"W:" are warnings.
// Evaluated for EVERY point (not only omitted/silenced ones) so the I-chart's
// EverFlags overlay colours the whole series when the master-data toggle is on.
// `liveConfig` (optional) filters the flags by the key's current config first.
export const everFlagsHasOOC = (data, liveConfig) =>
  getEverFlagsString(data, liveConfig).some((flag) => flag.trim().startsWith('OOC:'));

export const everFlagsHasWarning = (data, liveConfig) =>
  getEverFlagsString(data, liveConfig).some((flag) => {
    const trimmed = flag.trim();
    return trimmed.startsWith('Warnings:') || trimmed.startsWith('W:');
  });

// The config to filter everFlags by: the key's persisted Control Strategy doc, or
// null while the page still holds the placeholder default (no id / fullKey), so
// real flags aren't hidden by default rule values before the doc loads.
export const liveEverFlagsConfig = (config) =>
  config && config.id && config.fullKey ? config : null;

// Grouped everFlags lines for the EverFlags table column, e.g.
// ['Warnings: E-UCL', 'OOC: UCL']. Shown for EVERY row (not only omitted/silenced
// ones) so the table agrees with the I-chart overlay (#466), and filtered by the
// live config exactly like the chart colouring.
export const getEverFlagsDisplayGroups = (row, liveConfig) =>
  filterEverFlagsByConfig(row?.everFlags, liveConfig);

// Build the series rows (data point + control limits/stats) shown in the
// EverFlags I-chart tooltip, mirroring the default Recharts tooltip. The "Data"
// series (`resultOmits`) is intentionally blank for omitFromSqc === 'Y' rows —
// the exact points this tooltip covers — so it is always kept and falls back to
// the row's raw `result`. Other series are only included when they carry a real
// value. Returns raw `{ name, value, color }` rows; the caller sig-digit formats.
export const resolveEverFlagsTooltipRows = (payload = [], data = {}) =>
  (Array.isArray(payload) ? payload : [])
    .filter(
      (entry) =>
        entry.dataKey === 'resultOmits' ||
        (entry.value !== undefined && entry.value !== null && entry.value !== '')
    )
    .map((entry) => {
      const value =
        entry.dataKey === 'resultOmits' && (entry.value === '' || entry.value == null)
          ? data.result
          : entry.value;
      return { name: entry.name, value, color: entry.color };
    });
